import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import constraintsProductMicrocatsStore from 'shared/store/constraintsProductsMicrocatsStore';
import colorsStore from 'shared/store/colorsStore';

import styles from './ProductSearchInput.module.scss';

type ProductSearchInputProps = {
  onSelectProduct: (isin: string) => void;
  disabled?: boolean;
};

export const ProductSearchInput: React.FC<ProductSearchInputProps> = ({ onSelectProduct, disabled = false }) => {
  const { t } = useTranslation();
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const { fetchSearchProducts, productsSearchResults, resetProductsSearchResults, isSearchingProducts } =
    constraintsProductMicrocatsStore();

  // Search debounce
  useEffect(() => {
    if (searchTerm.length === 0) {
      resetProductsSearchResults();
      setShowResults(false);
      return;
    }

    const timer = setTimeout(() => {
      fetchSearchProducts(searchTerm);
      setShowResults(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectProduct = (isin: string) => {
    onSelectProduct(isin);
    setSearchTerm('');
    setShowResults(false);
    resetProductsSearchResults();
  };

  return (
    <div className={styles.searchContainer} ref={searchRef}>
      <div className={styles.searchInputWrapper}>
        {searchTerm.length === 0 ? (
          <Search className={styles.searchIcon} size={18} color={colorsConfig.FinecoBluePrimary} />
        ) : (
          <X
            className={styles.clearSearchIcon}
            size={18}
            color={colorsConfig.FinecoBluePrimary}
            onClick={() => setSearchTerm('')}
          />
        )}
        <input
          type="text"
          className={styles.searchInput}
          placeholder={t('constraints.products.searchFor')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={disabled}
        />
      </div>

      {isSearchingProducts && (
        <div className={styles.searchResults} style={{ backgroundColor: colorsConfig.white }}>
          <div className={styles.searching}>{t('constraints.products.searching')}</div>
        </div>
      )}

      {showResults && productsSearchResults && productsSearchResults.length > 0 && !isSearchingProducts && (
        <div className={styles.searchResults} style={{ backgroundColor: colorsConfig.white }}>
          {productsSearchResults.map((product: any) => (
            <div
              key={product.isin}
              className={styles.searchResultItem}
              onClick={() => handleSelectProduct(product.isin)}
            >
              <div className={styles.productInfo}>
                <span className={styles.productIsin}>{product.isin}</span>
                <span className={styles.separator}>-</span>
                <span className={styles.productName}>{product.descrizione_titolo || product.name || product.nome}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showResults &&
        searchTerm.length >= 2 &&
        productsSearchResults &&
        productsSearchResults.length === 0 &&
        !isSearchingProducts && (
          <div className={styles.searchResults} style={{ backgroundColor: colorsConfig.white }}>
            <div className={styles.noResults}>{t('constraints.products.noResults')}</div>
          </div>
        )}
    </div>
  );
};
